import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { drag as d3Drag, event as d3Event } from 'd3'
import useD3Selection from 'src/hooks/useD3Selection'
import { GroundPlanContext } from 'src/contexts'
import { useHandler } from 'src/hooks'
import styles from './Point.scss'

const Point = ({ point, xScale, yScale, onPointOver }) => {
  /**
   * Retrive plan props
   */

  const { disabled, isPrinting, onPointMove, onPointRemove, component: PointComponent } = GroundPlanContext.use()

  /**
   * Create local states
   */

  const [{ x, y }, setCoordinates] = React.useState(point.coordinates)
  const [isDragging, setDragging] = React.useState(false)
  const [isOver, setOver] = React.useState(false)

  /**
   * Create mouse handlers
   */

  const onDrag = useHandler(() => {
    if (!disabled) {
      setCoordinates({ x: xScale.invert(d3Event.x), y: yScale.invert(d3Event.y) })
      setDragging(true)
    }
  })

  const onDragEnd = useHandler(() => {
    if (!disabled && isDragging) {
      setDragging(false)
      onPointMove({ ...point, coordinates: { x, y } })
    }
  })

  const onMouseEnter = useHandler(() => {
    if (!isDragging) {
      setOver(true)
      onPointOver(point)
    }
  })

  const onMouseLeave = useHandler(() => {
    if (!isDragging) {
      setOver(false)
    }
  })

  /**
   * Update coordinates when point or scales changes
   */

  React.useEffect(() => {
    setCoordinates(point.coordinates)
  }, [point])

  /**
   * Create D3 drag behavior
   */

  const drag = React.useMemo(
    () =>
      d3Drag()
        .on('drag', onDrag)
        .on('end', onDragEnd),
    [onDrag, onDragEnd]
  )

  /**
   * Initialize D3 selection
   */

  const [ref, useSelectionEffect] = useD3Selection()

  /**
   * Apply drag behavior on selection
   */

  useSelectionEffect(
    selection => {
      drag(selection)
      return () => selection.on('.drag', null)
    },
    [drag]
  )

  return (
    <g
      className={classnames({ [styles.grabbable]: !disabled })}
      ref={ref}
      transform={`translate(${xScale(x)},${yScale(y)})`}
    >
      <PointComponent
        point={point}
        isDragging={isDragging}
        isPrinting={isPrinting}
        disabled={disabled}
        isOver={isOver}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointRemove={onPointRemove}
      />
    </g>
  )
}

Point.propTypes = {
  xScale: PropTypes.func.isRequired,
  yScale: PropTypes.func.isRequired,
  onPointOver: PropTypes.func.isRequired,
  point: PropTypes.object.isRequired,
}

export default Point
